/**
 * Self-hosted error reporter — phones home to /api/errors/log.
 * Captures:
 *  - window.onerror (uncaught sync errors)
 *  - unhandledrejection (uncaught promise rejections)
 *  - manual reportError() calls
 *  - axios response failures (via interceptor wired separately)
 *
 * Designed to be fail-soft: any error in the reporter itself is swallowed,
 * so logging never crashes the app.
 *
 * Includes:
 *  - de-dupe (same message within 30s won't double-report)
 *  - breadcrumbs (last 15 navigation/click events)
 *  - throttling (max 1 report per second; queues otherwise)
 */

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DEDUPE_WINDOW_MS = 30_000;
const THROTTLE_MS = 800;
const MAX_BREADCRUMBS = 15;

const recent = new Map();   // message -> timestamp
const breadcrumbs = [];     // [{ts, kind, data}]
let lastSent = 0;
let queue = [];

function _addBreadcrumb(kind, data) {
    breadcrumbs.push({ ts: Date.now(), kind, data });
    if (breadcrumbs.length > MAX_BREADCRUMBS) breadcrumbs.shift();
}

function _shouldDedupe(message) {
    const now = Date.now();
    const last = recent.get(message);
    if (last && (now - last) < DEDUPE_WINDOW_MS) return true;
    recent.set(message, now);
    // Trim old entries occasionally to prevent unbounded growth
    if (recent.size > 50) {
        const cutoff = now - DEDUPE_WINDOW_MS;
        for (const [k, v] of recent.entries()) {
            if (v < cutoff) recent.delete(k);
        }
    }
    return false;
}

async function _send(payload) {
    try {
        // Use sendBeacon if available (works during page unload too)
        if (navigator.sendBeacon) {
            const blob = new Blob([JSON.stringify(payload)], { type: "application/json" });
            const ok = navigator.sendBeacon(`${API}/errors/log`, blob);
            if (ok) return;
        }
        await fetch(`${API}/errors/log`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
            credentials: "include",
            keepalive: true,
        });
    } catch { /* swallow — never let logging crash the app */ }
}

function _flush() {
    if (queue.length === 0) return;
    const now = Date.now();
    if (now - lastSent < THROTTLE_MS) {
        setTimeout(_flush, THROTTLE_MS - (now - lastSent));
        return;
    }
    const next = queue.shift();
    lastSent = now;
    _send(next);
    if (queue.length > 0) setTimeout(_flush, THROTTLE_MS);
}

export function reportError(input) {
    try {
        const msg = String(input?.message || input || "Unknown error");
        if (_shouldDedupe(msg)) return;
        const payload = {
            source: "frontend",
            level: input?.level || "error",
            message: msg.slice(0, 1000),
            stack: (input?.stack ? String(input.stack) : "").slice(0, 8000),
            url: window.location.href.slice(0, 500),
            user_agent: navigator.userAgent.slice(0, 300),
            request_method: input?.request_method || null,
            request_path: input?.request_path || null,
            status_code: input?.status_code || null,
            response_body: input?.response_body ? String(input.response_body).slice(0, 2000) : null,
            breadcrumbs: breadcrumbs.slice(-MAX_BREADCRUMBS),
        };
        queue.push(payload);
        _flush();
    } catch { /* noop */ }
}

export function installErrorReporter() {
    // 1. Sync errors
    window.addEventListener("error", (ev) => {
        // Skip resource errors (img/script load fail) — not actionable usually
        if (ev.target && (ev.target.tagName === "IMG" || ev.target.tagName === "SCRIPT" || ev.target.tagName === "LINK")) return;
        reportError({
            message: ev.message || (ev.error && ev.error.message) || "Uncaught error",
            stack: ev.error && ev.error.stack,
        });
    });

    // 2. Promise rejections
    window.addEventListener("unhandledrejection", (ev) => {
        const reason = ev.reason;
        reportError({
            message: (reason && reason.message) || String(reason) || "Unhandled promise rejection",
            stack: reason && reason.stack,
            level: "warning",
        });
    });

    // 3. Breadcrumb: navigation
    if (typeof window !== "undefined") {
        let lastPath = window.location.pathname;
        const navObs = setInterval(() => {
            if (window.location.pathname !== lastPath) {
                _addBreadcrumb("navigation", { from: lastPath, to: window.location.pathname });
                lastPath = window.location.pathname;
            }
        }, 500);
        // 4. Breadcrumb: clicks on interactive elements with data-testid
        document.addEventListener("click", (e) => {
            const t = e.target.closest("[data-testid]");
            if (t) _addBreadcrumb("click", { testid: t.dataset.testid, text: (t.textContent || "").trim().slice(0, 50) });
        }, true);
        // Cleanup ref for SPA test environments
        window.__errorReporterCleanup = () => clearInterval(navObs);
    }
}

/**
 * Axios interceptor — call once after creating your client.
 * Sends 4xx/5xx response errors (except 401 which is normal auth flow).
 *
 * iter332 (Feb 28 2026) — Defense-in-depth: for known "anon-friendly"
 * endpoints (e.g. /me/selfies), RESOLVE the 401 into an empty response
 * instead of rejecting. Multiple call sites (PhotoUploadStep,
 * StepAIPreview, TryOnModal, FabricPicker) all fetch /me/selfies on
 * mount; even though each has its own .catch(), the rejection still
 * fires React's `unhandledrejection` handler in dev (CRA error overlay)
 * because the rejection is caught AFTER the microtask boundary the
 * overlay watches. Swallowing 401s for these endpoints at the axios
 * layer kills the noise at the root.
 */
const ANON_FRIENDLY_401_PATHS = [
    "/me/selfies",          // saved-selfies rail (Visual + Stepper + TryOn)
];

function _isAnonFriendly401(err) {
    if (err?.response?.status !== 401) return false;
    const url = err.config?.url || "";
    return ANON_FRIENDLY_401_PATHS.some((p) => url.includes(p));
}

export function installAxiosErrorInterceptor(axios) {
    axios.interceptors.response.use(
        (r) => r,
        (err) => {
            // iter332 — swallow 401s on known anon-friendly endpoints so the
            // CRA dev overlay + unhandledrejection telemetry don't fire.
            if (_isAnonFriendly401(err)) {
                return Promise.resolve({
                    data: [],
                    status: 401,
                    statusText: "Unauthorized (anon — swallowed)",
                    headers: {},
                    config: err.config || {},
                    _anonSwallowed: true,
                });
            }
            try {
                const status = err?.response?.status;
                // 401 = auth, 404 = often expected (notify-me, can-review etc)
                if (status && status !== 401 && status !== 404) {
                    reportError({
                        message: `API ${err.config?.method?.toUpperCase() || "?"} ${err.config?.url || "?"} → ${status}`,
                        stack: err.stack,
                        request_method: err.config?.method?.toUpperCase(),
                        request_path: err.config?.url,
                        status_code: status,
                        response_body: err.response?.data ? JSON.stringify(err.response.data) : null,
                        level: status >= 500 ? "error" : "warning",
                    });
                }
            } catch { /* noop */ }
            return Promise.reject(err);
        }
    );
}
